import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface TimelineStop {
  time: string;
  title: string;
  description: string;
  image?: string;
}

interface DayItem {
  day: number;
  title: string;
  subtitle?: string;
  content?: string;
  afternoonTitle?: string;
  afternoonContent?: string;
  images?: string[];
  timeline?: TimelineStop[];
  meals?: { breakfast?: boolean; lunch?: boolean; dinner?: boolean };
}

interface DayByDayProps {
  days: DayItem[];
}

function highlightTimes(text: string): ReactNode {
  const regex = /(\d{1,2}[hH:]\d{2}(?:\s*[-–]\s*\d{1,2}[hH:]\d{2})?)/g;
  const parts = text.split(regex);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <span
        key={i}
        className="mx-0.5 inline-block rounded-md bg-primary-100 px-1.5 py-0.5 align-baseline text-[12px] font-bold leading-none text-primary-700"
      >
        {part}
      </span>
    ) : (
      <span key={i}>{part}</span>
    )
  );
}

function renderRichContent(content: string) {
  const lines = content.split('\n');
  const result: JSX.Element[] = [];
  let buffer: string[] = [];
  let listKey = 0;

  const flushPara = () => {
    if (buffer.length > 0) {
      const text = buffer.join(' ');
      const isStrong = text.startsWith('<strong>') || text.startsWith('**');
      const cleanText = text.replace(/^\*\*/g, '').replace(/\*\*$/g, '').replace(/^<strong>/g, '').replace(/<\/strong>$/g, '');
      result.push(
        <p key={`p-${listKey++}`} className={`text-[13.5px] leading-[1.75] text-secondary-600 ${isStrong ? 'font-semibold text-secondary-900' : ''}`}>
          {highlightTimes(cleanText)}
        </p>
      );
      buffer = [];
    }
  };

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (trimmed.startsWith('- ')) {
      flushPara();
      const liText = trimmed.slice(2);
      const isBold = liText.startsWith('<strong>') || liText.startsWith('**');
      const cleanLi = liText.replace(/^\*\*/g, '').replace(/\*\*$/g, '').replace(/^<strong>/g, '').replace(/<\/strong>$/g, '');
      result.push(
        <div key={`li-${listKey++}`} className="flex items-start gap-2.5">
          <span className="mt-[5px] flex size-5 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-600">
            <i className="ri-arrow-right-s-line text-[14px]" />
          </span>
          <span className={`text-[13.5px] leading-[1.75] text-secondary-600 ${isBold ? 'font-semibold text-secondary-900' : ''}`}>
            {highlightTimes(cleanLi)}
          </span>
        </div>
      );
    } else if (trimmed === '') {
      flushPara();
    } else {
      buffer.push(trimmed);
    }
  });
  flushPara();

  return result;
}

function getPeriodIcon(title: string) {
  const t = title.toUpperCase();
  if (t.includes('MORNING') || t.includes('SÁNG')) return 'ri-sun-line';
  if (t.includes('AFTERNOON') || t.includes('CHIỀU')) return 'ri-sun-cloudy-line';
  if (t.includes('EVENING') || t.includes('NIGHT') || t.includes('TỐI') || t.includes('ĐÊM')) return 'ri-moon-line';
  return 'ri-map-pin-2-line';
}

function CaseCard({ title, content }: { title: string; content: string }) {
  return (
    <div className="overflow-hidden rounded-xl border border-secondary-200/80 bg-secondary-50/70">
      <div className="flex items-center gap-2.5 border-b border-secondary-200/70 px-4 py-3">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
          <i className={`${getPeriodIcon(title)} text-[16px]`} />
        </span>
        <h4 className="text-[13.5px] font-bold leading-snug text-secondary-900">{title}</h4>
      </div>
      <div className="space-y-3 px-4 py-4">
        {renderRichContent(content)}
      </div>
    </div>
  );
}

function TimelineCard({ stop, onOpen }: { stop: TimelineStop; onOpen?: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="overflow-hidden rounded-xl border border-secondary-200/80 bg-secondary-50/70">
      <div className="flex items-center gap-2.5 border-b border-secondary-200/70 px-4 py-3">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
          <i className="ri-time-line text-[16px]" />
        </span>
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2.5 gap-y-1.5">
          <span className="inline-flex shrink-0 items-center rounded-full bg-primary-100 px-2.5 py-1 text-[11px] font-bold text-primary-700">
            {stop.time}
          </span>
          <h4 className="min-w-0 text-[14px] font-bold leading-snug text-secondary-900">{stop.title}</h4>
        </div>
      </div>
      <div className="px-4 py-4">
        <p className="text-[13.5px] leading-[1.75] text-secondary-600">{stop.description}</p>
        {stop.image && (
          <button
            type="button"
            onClick={onOpen}
            className="group relative mt-3 block w-full overflow-hidden rounded-[10px] bg-secondary-100 cursor-pointer"
            aria-label={t('td.day.openPhotoFor', { title: stop.title })}
          >
            <div className="aspect-[16/9] w-full">
              <img
                src={stop.image}
                alt={stop.title}
                className="size-full object-cover object-top transition-transform duration-300 group-hover:scale-[1.03]"
                loading="lazy"
              />
            </div>
            <span className="absolute bottom-2.5 right-2.5 flex items-center gap-1.5 rounded-full bg-black/50 px-2.5 py-1.5 text-[11px] font-medium text-white backdrop-blur-sm">
              <i className="ri-zoom-in-line text-xs" />
              {t('td.day.viewPhoto')}
            </span>
          </button>
        )}
      </div>
    </div>
  );
}

export default function DayByDay({ days }: DayByDayProps) {
  const { t } = useTranslation();
  const [expandedDays, setExpandedDays] = useState<Set<number>>(new Set([1]));
  const [lightbox, setLightbox] = useState<{ images: string[]; index: number } | null>(null);

  const toggleAll = () => {
    if (expandedDays.size > 0) {
      setExpandedDays(new Set());
    } else {
      setExpandedDays(new Set(days.map((d) => d.day)));
    }
  };

  const toggleDay = (day: number) => {
    const next = new Set(expandedDays);
    if (next.has(day)) next.delete(day);
    else next.add(day);
    setExpandedDays(next);
  };

  const openLightbox = (images: string[], index: number) => {
    setLightbox({ images, index });
  };

  const closeLightbox = () => setLightbox(null);

  const goPrev = () => {
    if (!lightbox) return;
    setLightbox({ ...lightbox, index: (lightbox.index - 1 + lightbox.images.length) % lightbox.images.length });
  };

  const goNext = () => {
    if (!lightbox) return;
    setLightbox({ ...lightbox, index: (lightbox.index + 1) % lightbox.images.length });
  };

  return (
    <section className="rounded-[14px] border border-background-200 bg-white px-[22px] py-1.5">
      {/* Header */}
      <div className="flex items-center justify-between py-3.5">
        <h2 className="text-[20px] font-bold leading-[1.3] tracking-[-0.01em] text-foreground-950">{t('td.day.title')}</h2>
        <button
          onClick={toggleAll}
          className="flex items-center gap-1 text-[12px] text-secondary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          {expandedDays.size > 0 ? t('td.day.collapse') : t('td.day.expand')}
          <i className={expandedDays.size > 0 ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
        </button>
      </div>
      <p className="mb-3 flex items-start gap-3 text-[12px] leading-[1.65] text-secondary-500">
        <i className="ri-information-line text-secondary-400 shrink-0 mt-0.5" />
        <span>{t('td.day.intro')}</span>
      </p>

      {/* Days */}
      <div className="border-t-0 pt-4">
        {days.map((item, idx) => {
          const isOpen = expandedDays.has(item.day);
          const m = item.meals || {};
          const hasTimeline = !!item.timeline && item.timeline.length > 0;
          const timelineImages: string[] = hasTimeline
            ? (item.timeline || []).map((s) => s.image).filter((x): x is string => !!x)
            : [];

          return (
            <div key={item.day} className={`${idx > 0 ? 'border-t border-background-200/70 pt-4' : ''}`}>
              <button
                type="button"
                onClick={() => toggleDay(item.day)}
                className="flex w-full items-start justify-between gap-3.5 text-left cursor-pointer"
              >
                <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2.5 gap-y-2">
                  <span className="shrink-0 rounded-md bg-primary-100 px-2 py-1 text-[10.5px] font-extrabold tracking-[0.1em] text-primary-600">
                    {t('td.day.dayLabel', { n: item.day })}
                  </span>
                  <span className="min-w-0 text-[16px] font-semibold text-foreground-950">{item.title}</span>
                  <span className="inline-flex shrink-0 gap-1">
                    {['B', 'L', 'D'].map((letter, i) => {
                      const active = (i === 0 && m.breakfast) || (i === 1 && m.lunch) || (i === 2 && m.dinner);
                      return (
                        <span
                          key={letter}
                          title={i === 0 ? t('td.day.breakfast') : i === 1 ? t('td.day.lunch') : t('td.day.dinner')}
                          className={`grid size-[18px] place-items-center rounded text-[10px] font-extrabold ${
                            active
                              ? 'bg-primary-500 text-white'
                              : 'border border-background-200 bg-white text-secondary-400'
                          }`}
                        >
                          {letter}
                        </span>
                      );
                    })}
                  </span>
                </span>
                <i className={`text-secondary-400 text-lg shrink-0 mt-1 ${isOpen ? 'ri-subtract-line' : 'ri-add-line'}`} />
              </button>

              {isOpen && (
                hasTimeline ? (
                  <div className="flex flex-col gap-4 pb-6 pt-4">
                    {(item.timeline || []).map((stop, si) => {
                      const imageIndex = stop.image ? timelineImages.indexOf(stop.image) : -1;
                      return (
                        <TimelineCard
                          key={si}
                          stop={stop}
                          onOpen={stop.image ? () => openLightbox(timelineImages, imageIndex) : undefined}
                        />
                      );
                    })}
                  </div>
                ) : (
                  <div className="flex flex-col gap-4 pb-6 pt-4">
                    {/* Morning / First Section */}
                    {item.subtitle && item.content && (
                      <CaseCard title={item.subtitle} content={item.content} />
                    )}

                    {/* Day Images */}
                    {item.images && item.images.length > 0 && (
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                        {item.images.map((img, imgIdx) => (
                          <button
                            key={imgIdx}
                            type="button"
                            onClick={() => openLightbox(item.images!, imgIdx)}
                            className="group relative block aspect-[4/3] cursor-pointer overflow-hidden rounded-[9px] bg-secondary-100"
                            aria-label={t('td.day.openPhoto', { n: imgIdx + 1 })}
                          >
                            <img
                              src={img}
                              alt={`Itinerary photo ${imgIdx + 1}`}
                              className="size-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
                              loading="lazy"
                            />
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Afternoon / Second Section */}
                    {item.afternoonTitle && item.afternoonContent && (
                      <CaseCard title={item.afternoonTitle} content={item.afternoonContent} />
                    )}
                  </div>
                )
              )}
            </div>
          );
        })}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 p-4"
          onClick={closeLightbox}
        >
          <div className="relative w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightbox.images[lightbox.index]}
              alt={`Itinerary photo ${lightbox.index + 1}`}
              className="mx-auto max-h-[80vh] w-auto max-w-full rounded-lg object-contain"
            />
            {lightbox.images.length > 1 && (
              <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-2">
                {lightbox.images.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setLightbox({ ...lightbox, index: i })}
                    className={`h-2 w-2 cursor-pointer rounded-full transition-all ${
                      i === lightbox.index ? 'scale-125 bg-white' : 'bg-white/50'
                    }`}
                  />
                ))}
              </div>
            )}
            <button
              onClick={closeLightbox}
              className="absolute -top-2 right-0 flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-white/20 text-white transition-colors hover:bg-white/30"
              aria-label={t('td.day.close')}
            >
              <i className="ri-close-line text-xl" />
            </button>
            {lightbox.images.length > 1 && lightbox.index > 0 && (
              <button
                onClick={goPrev}
                className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white/20 text-white transition-colors hover:bg-white/30"
                aria-label={t('td.day.previous')}
              >
                <i className="ri-arrow-left-s-line text-xl" />
              </button>
            )}
            {lightbox.images.length > 1 && lightbox.index < lightbox.images.length - 1 && (
              <button
                onClick={goNext}
                className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-white/20 text-white transition-colors hover:bg-white/30"
                aria-label={t('td.day.next')}
              >
                <i className="ri-arrow-right-s-line text-xl" />
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}